import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, FileText, Link2, Type, Upload, Loader2, Database, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter,
} from "@/components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { getKbEntries, createKbEntry, updateKbEntry, toggleKbEntry, rereadKbEntry, deleteKbEntry } from "@/lib/dentiumApi";

const SOURCE_META = {
  text: { label: "Texto", icon: Type, color: "#1A1A1A" },
  url: { label: "URL", icon: Link2, color: "#2563EB" },
  file: { label: "Archivo", icon: FileText, color: "#B45309" },
};

const emptyForm = { name: "", description: "", source: "text", content: "", file: null };

export default function ConocimientoPage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = crear, obj = editar
  const [form, setForm] = useState(emptyForm);
  const [toDelete, setToDelete] = useState(null);

  const { data: items = [] } = useQuery({ queryKey: ["kb-entries"], queryFn: getKbEntries });
  const refresh = () => qc.invalidateQueries({ queryKey: ["kb-entries"] });

  const openCreate = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (it) => {
    setEditing(it);
    setForm({ name: it.name, description: it.description || "", source: it.source || "text", content: it.content || "", file: null });
    setOpen(true);
  };

  const buildFormData = () => {
    const fd = new FormData();
    fd.append("name", form.name.trim());
    fd.append("description", form.description.trim());
    fd.append("source", form.source);
    fd.append("content", form.source === "file" ? "" : form.content.trim());
    if (form.source === "file" && form.file) fd.append("file", form.file);
    return fd;
  };

  const save = useMutation({
    mutationFn: () => (editing ? updateKbEntry(editing.id, buildFormData()) : createKbEntry(buildFormData())),
    onSuccess: () => {
      refresh();
      setOpen(false);
      toast.success(editing ? "Base de conocimientos actualizada" : "Base de conocimientos agregada", {
        description: "El bot ya usa la información actualizada en el siguiente mensaje.",
      });
    },
    onError: (e) => toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "No se pudo guardar"),
  });

  const toggle = useMutation({
    mutationFn: (id) => toggleKbEntry(id),
    onSuccess: (d) => { refresh(); toast.success(d.active ? "Activada" : "Desactivada"); },
    onError: () => toast.error("No se pudo cambiar el estado"),
  });

  const remove = useMutation({
    mutationFn: (id) => deleteKbEntry(id),
    onSuccess: () => { refresh(); setToDelete(null); toast.success("Eliminada", { description: "El bot ya no usará esta información." }); },
    onError: () => toast.error("No se pudo eliminar"),
  });

  const reread = useMutation({
    mutationFn: (id) => rereadKbEntry(id),
    onSuccess: (d) => {
      refresh();
      if (d.extracted_chars) toast.success("Contenido releído", { description: `El bot ya usa ${d.extracted_chars.toLocaleString("es-ES")} caracteres actualizados.` });
      else toast.warning("No se pudo leer el contenido", { description: "Comprueba que la URL es pública o que el archivo no está protegido." });
    },
    onError: () => toast.error("No se pudo volver a leer"),
  });

  const canSave = form.name.trim() &&
    (form.source === "text" ? form.content.trim() :
     form.source === "url" ? form.content.trim() :
     (form.file || (editing && editing.source === "file" && editing.has_file)));

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1100px] mx-auto overflow-y-auto h-full" data-testid="conocimiento-page">
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <p className="text-sm text-[#8A7E70] max-w-lg">
          Gestiona las bases de conocimiento que el bot consulta antes de responder. Solo las <b>Activas</b> se usan.
        </p>
        <Button
          data-testid="btn-add-knowledge"
          onClick={openCreate}
          className="h-11 px-5 bg-[#1A1A1A] hover:bg-black text-white rounded-xl shrink-0"
        >
          <Plus className="h-5 w-5 mr-2" /> Agregar base de conocimientos
        </Button>
      </div>

      <div className="rounded-2xl border border-[#E7E2DA] bg-white overflow-hidden" data-testid="knowledge-table">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#FAF8F5] hover:bg-[#FAF8F5] border-[#EDE7DE]">
              <TableHead className="text-[#6B6157] font-semibold">Nombre del modelo</TableHead>
              <TableHead className="text-[#6B6157] font-semibold hidden md:table-cell">Propietario</TableHead>
              <TableHead className="text-[#6B6157] font-semibold">Estatus</TableHead>
              <TableHead className="text-[#6B6157] font-semibold text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-12 text-sm text-[#A79E90]">
                  Aún no hay bases de conocimiento. Pulsa “Agregar base de conocimientos”.
                </TableCell>
              </TableRow>
            )}
            {items.map((it) => {
              const meta = SOURCE_META[it.source] || SOURCE_META.text;
              const SIcon = meta.icon;
              return (
                <TableRow key={it.id} data-testid="knowledge-row" className="border-[#EDE7DE]">
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <span className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${meta.color}15`, color: meta.color }}>
                        <SIcon className="h-4.5 w-4.5" />
                      </span>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-[#1A1A1A] truncate">{it.name}</div>
                        <div className="text-xs text-[#A79E90] truncate max-w-[280px]">{it.description || meta.label}</div>
                        {it.source !== "text" && (
                          <div data-testid={`kb-extract-status-${it.id}`} className={`text-[11px] mt-0.5 ${it.extracted_chars ? "text-emerald-700" : "text-amber-700"}`}>
                            {it.extracted_chars ? `Contenido leído por el bot (${it.extracted_chars.toLocaleString("es-ES")} caracteres)` : "No se pudo leer el contenido: el bot solo usará el nombre y la descripción"}
                          </div>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-sm text-[#6B6157] truncate max-w-[180px]">{it.owner}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch
                        data-testid="kb-status-switch"
                        checked={it.active}
                        onCheckedChange={() => toggle.mutate(it.id)}
                      />
                      <span className={`text-xs font-medium ${it.active ? "text-emerald-600" : "text-[#A79E90]"}`}>
                        {it.active ? "Activo" : "Inactivo"}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {it.source !== "text" && (
                        <Button
                          variant="ghost" size="sm"
                          data-testid="btn-reread-knowledge"
                          title="Volver a leer el contenido"
                          disabled={reread.isPending && reread.variables === it.id}
                          onClick={() => reread.mutate(it.id)}
                          className="h-9 px-2.5 text-[#6B6157] hover:bg-[#F1EBE1]"
                        >
                          <RefreshCw className={`h-4 w-4 md:mr-1.5 ${reread.isPending && reread.variables === it.id ? "animate-spin" : ""}`} />
                          <span className="hidden md:inline">Releer</span>
                        </Button>
                      )}
                      <Button
                        variant="ghost" size="sm"
                        data-testid="btn-edit-knowledge"
                        onClick={() => openEdit(it)}
                        className="h-9 px-2.5 text-[#6B6157] hover:bg-[#F1EBE1]"
                      >
                        <Pencil className="h-4 w-4 md:mr-1.5" /> <span className="hidden md:inline">Editar</span>
                      </Button>
                      <Button
                        variant="ghost" size="sm"
                        data-testid="btn-delete-knowledge"
                        onClick={() => setToDelete(it)}
                        className="h-9 px-2.5 text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4 md:mr-1.5" /> <span className="hidden md:inline">Eliminar</span>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Panel lateral de alta/edición */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="knowledge-sheet">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Database className="h-5 w-5" /> {editing ? "Editar base de conocimientos" : "Agregar base de conocimientos"}
            </SheetTitle>
            <SheetDescription>Define el contenido que el bot usará para responder.</SheetDescription>
          </SheetHeader>

          <div className="space-y-4 py-5">
            <div>
              <Label className="mb-1.5 block">Nombre del modelo</Label>
              <Input
                data-testid="kb-name-input"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Ej.: Tratamientos de ortodoncia"
                maxLength={200}
              />
            </div>
            <div>
              <Label className="mb-1.5 block">Descripción</Label>
              <Input
                data-testid="kb-description-input"
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Breve descripción (opcional)"
                maxLength={300}
              />
            </div>
            <div>
              <Label className="mb-1.5 block">Origen</Label>
              <Select value={form.source} onValueChange={(v) => setForm((f) => ({ ...f, source: v }))}>
                <SelectTrigger data-testid="kb-source-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="file" data-testid="kb-source-file">Archivo</SelectItem>
                  <SelectItem value="url" data-testid="kb-source-url">URL</SelectItem>
                  <SelectItem value="text" data-testid="kb-source-text">Texto</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {form.source === "file" && (
              <div data-testid="kb-file-field">
                <Label className="mb-1.5 block">Archivo (PDF, Word, Excel, PowerPoint, texto o imagen)</Label>
                <label className="flex items-center gap-2 h-11 rounded-md border border-dashed border-[#D8CDBB] bg-[#FAF8F5] px-3 cursor-pointer text-sm text-[#6B6157] hover:bg-[#F1EBE1] transition-colors">
                  <Upload className="h-4 w-4" />
                  <span className="truncate">
                    {form.file ? form.file.name : (editing && editing.file_name) ? `Actual: ${editing.file_name}` : "Seleccionar archivo…"}
                  </span>
                  <input
                    data-testid="kb-file-input"
                    type="file"
                    accept=".pdf,.doc,.docx,.xlsx,.pptx,.txt,.md,.csv,.html,.htm,.png,.jpg,.jpeg,.webp"
                    className="hidden"
                    onChange={(e) => setForm((f) => ({ ...f, file: e.target.files?.[0] || null }))}
                  />
                </label>
                <p className="text-[11px] text-[#A79E90] mt-1.5">El bot leerá todo el contenido, también PDF escaneados e imágenes (máx. 8 MB). Las URLs se releen cada día.</p>
              </div>
            )}

            {form.source === "url" && (
              <div data-testid="kb-url-field">
                <Label className="mb-1.5 block">Enlace (URL)</Label>
                <Input
                  data-testid="kb-url-input"
                  value={form.content}
                  onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
                  placeholder="https://www.ejemplo.com/pagina"
                />
              </div>
            )}

            {form.source === "text" && (
              <div data-testid="kb-text-field">
                <Label className="mb-1.5 block">Contenido</Label>
                <Textarea
                  data-testid="kb-text-input"
                  value={form.content}
                  onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
                  placeholder="Escribe o pega aquí el contenido que debe conocer el bot…"
                  rows={7}
                  maxLength={5000}
                  className="resize-none"
                />
              </div>
            )}
          </div>

          <SheetFooter className="flex-row gap-2 justify-end">
            <Button variant="outline" data-testid="kb-cancel-btn" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button
              data-testid="kb-save-btn"
              onClick={() => canSave && save.mutate()}
              disabled={save.isPending || !canSave}
              className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-60"
            >
              {save.isPending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Guardando…</> : "Guardar"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Confirmación de borrado */}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="kb-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar “{toDelete?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer. El bot dejará de usar esta información.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="kb-delete-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              data-testid="kb-delete-confirm"
              onClick={() => remove.mutate(toDelete.id)}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
